// perfilesDocs.js — Reglas comunes a las secciones que agrupan contenidos por
// perfil (subrol): qué perfiles se muestran, qué contenidos entran en cada uno y
// en qué orden. Lo usan la lista del programa y el bloque de docentes, para que
// el módulo 3 sea el mismo en las dos y el orden se configure una sola vez.
import { useEffect, useMemo, useState } from 'react';
import { consultaService, profileService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export const useBloquesPorPerfil = (c = {}, proyecto) => {
  const { user } = useAuth();

  const documentos = useMemo(() => proyecto?.documentos || [], [proyecto]);
  const subrolesCatalogo = useMemo(
    () => (Array.isArray(proyecto?.config_json?.subroles) ? proyecto.config_json.subroles : []),
    [proyecto]
  );

  const esAdmin = !!user && (user.role === 'admin' || user.role === 'mentor');
  const verTodos = esAdmin && c.admin_ve_todos !== false;

  // El objeto de sesión puede venir de un login viejo (sin subrol).
  const [subrolUsuario, setSubrolUsuario] = useState(user?.subrol || '');
  useEffect(() => {
    if (!user || user.subrol !== undefined) { setSubrolUsuario(user?.subrol || ''); return undefined; }
    let vivo = true;
    profileService.getProfile()
      .then(r => { if (vivo) setSubrolUsuario(r.data?.user?.subrol || ''); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user]);

  // Contenidos que el usuario puede ver (documentos.php ya filtra por rol, país,
  // idioma, convenio y perfil). El admin ve el catálogo completo.
  const [permitidos, setPermitidos] = useState(null);
  const [esperando, setEsperando] = useState(false);
  useEffect(() => {
    if (!user || verTodos) { setPermitidos(null); setEsperando(false); return undefined; }
    let vivo = true;
    setEsperando(true);
    consultaService.getDocumentos()
      .then(r => { if (vivo) setPermitidos(new Set((r.data || []).map(d => String(d.id)))); })
      .catch(() => { if (vivo) setPermitidos(null); })
      .finally(() => { if (vivo) setEsperando(false); });
    return () => { vivo = false; };
  }, [user, verTodos]);

  const ocultos = Array.isArray(c.perfiles_ocultos) ? c.perfiles_ocultos.map(String) : [];
  const ordenPerfiles = Array.isArray(c.perfiles_orden) ? c.perfiles_orden.map(String) : [];
  const catalogo = subrolesCatalogo.filter(s => s && s.key && !ocultos.includes(String(s.key)));
  const ordenados = ordenPerfiles.length > 0
    ? [
        ...ordenPerfiles.map(k => catalogo.find(s => String(s.key) === k)).filter(Boolean),
        ...catalogo.filter(s => !ordenPerfiles.includes(String(s.key))),
      ]
    : catalogo;

  const perfilesVisibles = verTodos
    ? ordenados
    : ordenados.filter(s => String(s.key) === String(subrolUsuario));
  const sinPerfil = perfilesVisibles.length === 0;

  const ordenarPerfil = (key, docs) => {
    const lista = (c.orden_por_perfil || {})[key];
    if (!Array.isArray(lista) || lista.length === 0) return docs;
    const pos = new Map(lista.map((id, i) => [String(id), i]));
    return [...docs].sort((a, b) => {
      const pa = pos.has(String(a.id)) ? pos.get(String(a.id)) : Number.MAX_SAFE_INTEGER;
      const pb = pos.has(String(b.id)) ? pos.get(String(b.id)) : Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });
  };

  // Un contenido sin perfiles asignados aparece en todos (misma regla que
  // documentos.php: sin filas en documento_subroles = visible para todos).
  const docsDelPerfil = (key) => documentos.filter(d => {
    const subs = Array.isArray(d.subroles) ? d.subroles.map(String) : [];
    if (subs.length > 0 && !subs.includes(String(key))) return false;
    if (permitidos && !permitidos.has(String(d.id))) return false;
    return true;
  });

  const bloques = sinPerfil
    ? [{ key: '', label: '', docs: documentos.filter(d => !permitidos || permitidos.has(String(d.id))) }]
    : perfilesVisibles.map(s => ({
        key: String(s.key),
        label: s.label || s.key,
        docs: ordenarPerfil(s.key, docsDelPerfil(s.key)),
      }));

  return {
    bloques,
    verTodos,
    sinPerfil,
    // true mientras se resuelve qué puede ver el usuario: pintar antes mostraría
    // contenidos que luego desaparecen.
    cargando: esperando || documentos.length === 0,
  };
};

// Docentes de una lista de contenidos, en el orden de los contenidos y sin
// repetir a quien dicta varios módulos.
export const docentesDeDocs = (docs) => {
  const vistos = new Set();
  const salida = [];
  (docs || []).forEach(d => {
    const sel = Array.isArray(d.docentes_sel) ? d.docentes_sel : [];
    const lista = sel.length > 0
      ? sel
      : (d.docente ? [{ id: `doc_${d.id}`, nombre: d.docente, foto: d.docente_foto, descripcion: '' }] : []);
    lista.forEach(doc => {
      const clave = String(doc.id != null ? doc.id : doc.nombre);
      if (!doc.nombre || vistos.has(clave)) return;
      vistos.add(clave);
      salida.push(doc);
    });
  });
  return salida;
};
